import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Check, AlertCircle, Loader2, Download, Euro, ChevronLeft, Mail } from 'lucide-react';
import { PaymentPlanBlock } from './PaymentPlan';
import { StandardTermsBlock } from './StandardTerms';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtEuro = (v) => `€ ${(Number(v) || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const COMPANY = {
  name: 'Cardoso Bouw en Onderhoud',
  address: 'Vuntuslaan 69',
  city: '1231 NR Loosdrecht',
  phone: '0685071009',
  email: 'info@cardosobouw.nl',
  kvk: '85310646',
  btw_id: 'NL004077138B58',
  iban: 'NL33INGB0709828160',
  bic: 'INGBNL2A',
};

/**
 * Public invoice viewer at /factuur/:token
 * Client can view the invoice, download PDF, and pay the restbedrag via Mollie.
 */
const PublicInvoice = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payLoading, setPayLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const pdfRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [iRes, pRes] = await Promise.all([
          axios.get(`${API}/public/invoices/${token}`),
          axios.get(`${API}/public/invoices/${token}/payment-status`),
        ]);
        if (cancelled) return;
        setInvoice(iRes.data);
        setPaymentStatus(pRes.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Factuur niet gevonden of link verlopen');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, searchParams]);

  const handleDownloadPdf = () => {
    if (!pdfRef.current || !invoice) {
      toast.error('PDF nog niet beschikbaar');
      return;
    }
    const filename = `Factuur_${invoice.number}_${(invoice.client_name || '').replace(/[^a-z0-9]/gi, '_')}.pdf`;
    const opts = {
      margin: 0, filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'], before: '.html2pdf__page-break' },
    };
    html2pdf().set(opts).from(pdfRef.current).save()
      .then(() => toast.success('PDF gedownload'))
      .catch(() => toast.error('Fout bij downloaden PDF'));
  };

  const handleEmailToSelf = async () => {
    if (!pdfRef.current || !invoice) {
      toast.error('PDF nog niet beschikbaar');
      return;
    }
    if (!invoice.client_email) {
      toast.error('Er staat geen e-mailadres op deze factuur — neem contact met ons op.');
      return;
    }
    setEmailLoading(true);
    try {
      const filename = `Factuur_${invoice.number}_${(invoice.client_name || '').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      const opts = {
        margin: 0,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'], before: '.html2pdf__page-break' },
      };
      const pdfDataUri = await html2pdf().set(opts).from(pdfRef.current).outputPdf('datauristring');
      await axios.post(`${API}/public/invoices/${token}/email-to-self`, {
        pdf_base64: pdfDataUri,
        pdf_filename: filename,
      });
      toast.success(`Factuur verzonden naar ${invoice.client_email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versturen mislukt');
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePay = async () => {
    setPayLoading(true);
    try {
      const r = await axios.post(`${API}/public/invoices/${token}/restbedrag-payment`, {});
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('Geen betaal-link ontvangen');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Betaling aanmaken mislukt');
    } finally {
      setPayLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="w-8 h-8 animate-spin text-[#3da5a5]" />
    </div>
  );

  if (error || !invoice) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <Card className="max-w-md w-full"><CardContent className="py-10 text-center space-y-3">
        <AlertCircle className="w-14 h-14 text-orange-500 mx-auto" />
        <h2 className="text-xl font-bold text-gray-800">Factuur niet beschikbaar</h2>
        <p className="text-sm text-gray-600">{error || 'Onbekende fout'}</p>
      </CardContent></Card>
    </div>
  );

  const ps = paymentStatus || {};
  const isPaid = ps.restbedrag_status === 'paid';
  const isOpen = ps.restbedrag_status === 'open';
  // Prefer the server-computed `restbedrag_open` (subtracts ALL paid termijnen of the
  // payment plan, not just the single down_payment). Falls back to the old calc.
  const restAmount = ps.restbedrag_open != null
    ? Number(ps.restbedrag_open).toFixed(2)
    : (ps.restbedrag_amount || (Number(ps.total_incl || invoice.total_incl) - Number(ps.already_paid || ps.down_payment_amount || 0)).toFixed(2));
  const alreadyPaidAmount = ps.already_paid != null ? Number(ps.already_paid) : Number(ps.down_payment_amount || 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {searchParams.get('from') === 'portal' && (
          <button
            onClick={() => navigate('/client/dashboard')}
            className="flex items-center gap-2 text-sm text-[#1a5276] hover:text-[#3da5a5]"
            data-testid="back-to-portal-invoice"
          >
            <ChevronLeft className="w-4 h-4" /> Terug naar mijn portaal
          </button>
        )}
        {/* PDF-printable container — uses the exact same inline-style template as the
            admin /invoices PDF so the klant downloads the same document as the admin. */}
        <div ref={pdfRef}>
          <div style={{ fontFamily: 'Arial, sans-serif', padding: '40px', maxWidth: '800px', margin: '0 auto', color: '#333', background: 'white' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
              <div>
                <h2 style={{ color: invoice.type === 'credit' ? '#c0392b' : '#1a5276', fontSize: '22px', margin: '0 0 20px 0' }}>
                  {invoice.type === 'credit' ? 'Creditfactuur' : 'Factuur'} {invoice.number}
                </h2>
                {invoice.credits_invoice && <p style={{ margin: '2px 0', fontSize: '13px', color: '#c0392b' }}><strong>Credit voor factuur:</strong> {invoice.credits_invoice}</p>}
                <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Naam Klant:</strong> {invoice.client_name}</p>
                {invoice.client_address && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Adres Klant:</strong> {invoice.client_address}</p>}
                {invoice.client_city && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Plaats Klant:</strong> {invoice.client_city}</p>}
                {invoice.client_email && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>E-mail:</strong> {invoice.client_email}</p>}
                {invoice.client_phone && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Telefoon:</strong> {invoice.client_phone}</p>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <img src="/logo-cardoso.jpeg" alt="Logo" style={{ width: '120px', marginBottom: '10px' }} onError={(e) => { e.target.style.display = 'none'; }} />
                <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.name}</p>
                <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.address}</p>
                <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.city}</p>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
              <p style={{ margin: '2px 0', fontSize: '13px' }}>
                {invoice.description && <><strong>Omschrijving factuur:</strong> {invoice.description}</>}
                {!invoice.description && invoice.from_quote && <><strong>Van offerte:</strong> {invoice.from_quote}</>}
              </p>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Factuurdatum:</strong> {invoice.invoice_date}</p>
                {invoice.due_date && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Vervaldatum:</strong> {invoice.due_date}</p>}
              </div>
            </div>

            {(() => { const hasDisc = (invoice.items || []).some(it => (it.discount || 0) > 0); return (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px' }}>
              <thead><tr style={{ background: '#1a5276', color: 'white' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Omschrijving</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Aantal</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Bedrag</th>
                {hasDisc && <th style={{ padding: '8px', textAlign: 'right' }}>Korting</th>}
                <th style={{ padding: '8px', textAlign: 'right' }}>BTW</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Totaal excl. BTW</th>
              </tr></thead>
              <tbody>
                {(invoice.items || []).map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px' }}>{item.description}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{item.quantity} {item.unit || ''}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{fmtEuro(item.unit_price)}</td>
                    {hasDisc && <td style={{ padding: '8px', textAlign: 'right', color: (item.discount || 0) > 0 ? '#dc2626' : '#9ca3af' }}>{(item.discount || 0) > 0 ? `${item.discount}%` : '-'}</td>}
                    <td style={{ padding: '8px', textAlign: 'right' }}>{item.btw}%</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{fmtEuro(item.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            ); })()}

            {(() => { const isVerlegd = (invoice.btw_status || '').toLowerCase() === 'verlegd'; const gd = invoice.general_discount || 0; return (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
              <div style={{ width: '300px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}><span>Subtotaal</span><strong>{fmtEuro(invoice.subtotal)}</strong></div>
                {gd > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', color: '#dc2626' }}><span>Korting</span><span>-{fmtEuro(gd)}</span></div>
                )}
                {!isVerlegd && Object.entries(invoice.btw_totals || {}).map(([rate, amt]) => (
                  <div key={rate} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}><span>BTW {rate}</span><span>{fmtEuro(amt)}</span></div>
                ))}
                {isVerlegd && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#92400e', fontStyle: 'italic' }}><span>BTW verlegd</span><span>€ 0,00</span></div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '2px solid #1a5276', fontSize: '16px', fontWeight: 'bold', color: '#1a5276' }}><span>Totaal{isVerlegd ? '' : ' incl. BTW'}</span><span>{fmtEuro(invoice.total_incl)}</span></div>
                {alreadyPaidAmount > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', color: '#0a7a0a' }}><span>Reeds voldaan</span><span>-{fmtEuro(alreadyPaidAmount)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid #ccc', fontSize: '14px', fontWeight: 'bold', color: '#15803d' }}><span>Nog te voldoen</span><span>{fmtEuro(restAmount)}</span></div>
                  </>
                )}
              </div>
            </div>
            ); })()}

            {(invoice.btw_status || '').toLowerCase() === 'verlegd' && (
              <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '4px', fontSize: '12px', marginBottom: '15px', color: '#78350f' }}>
                <strong>BTW verlegd.</strong> Overeenkomstig artikel 12, vijfde lid van de Wet op de omzetbelasting 1968 is de heffing van btw verlegd naar de afnemer.
              </div>
            )}

            <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '4px', fontSize: '12px', marginBottom: '20px' }}>
              {invoice.notes || 'We verzoeken u vriendelijk het bovenstaande bedrag voor de genoemde vervaldatum te voldoen op onze bankrekening onder vermelding van het factuurnummer.'}
            </div>

            <PaymentPlanBlock plan={invoice.payment_plan} totalIncl={invoice.total_incl} documentNumber={invoice.number} documentType="Factuur" />
            <StandardTermsBlock />

            <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px', fontSize: '11px', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <p>{COMPANY.name} · {COMPANY.address}, {COMPANY.city}</p>
                <p>Mobiel: {COMPANY.phone} · E-mail: {COMPANY.email}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p>KVK: {COMPANY.kvk} · BTW-id: {COMPANY.btw_id}</p>
                <p>IBAN: {COMPANY.iban} · BIC: {COMPANY.bic}</p>
              </div>
            </div>
          </div>
        </div>
        {/* end pdfRef */}

        <div className="flex flex-col sm:flex-row justify-end gap-2">
          <Button
            onClick={handleEmailToSelf}
            disabled={emailLoading || !invoice.client_email}
            variant="outline"
            className="border-[#3da5a5] text-[#3da5a5] hover:bg-[#3da5a5]/5"
            title={invoice.client_email ? `Stuurt naar ${invoice.client_email}` : 'Geen e-mailadres bekend'}
            data-testid="email-invoice-to-self"
          >
            {emailLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Versturen...</> : <><Mail className="w-4 h-4 mr-2" /> Stuur naar mijn e-mail</>}
          </Button>
          <Button onClick={handleDownloadPdf} variant="outline" className="border-[#1a5276] text-[#1a5276] hover:bg-[#1a5276]/5" data-testid="download-public-invoice-pdf">
            <Download className="w-4 h-4 mr-2" /> PDF downloaden
          </Button>
        </div>

        <Card className="border-2 border-[#3da5a5]" data-testid="invoice-payment-block">
          <CardContent className="py-6 space-y-4">
            <h3 className="text-lg font-bold text-[#1a5276] flex items-center gap-2">
              <Euro className="w-5 h-5" /> Betaling
            </h3>
            {isPaid ? (
              <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800 flex items-center gap-2" data-testid="invoice-paid-badge">
                <Check className="w-5 h-5" /> Factuur voldaan
                {ps.restbedrag_paid_at && <span className="text-xs text-green-600">({new Date(ps.restbedrag_paid_at).toLocaleString('nl-NL')})</span>}
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-700">
                  Te betalen: <strong className="text-[#15803d] text-base">{fmtEuro(restAmount)}</strong>
                  {alreadyPaidAmount > 0 && (
                    <span className="text-xs text-gray-500"> (van totaal {fmtEuro(invoice.total_incl)} — reeds voldaan {fmtEuro(alreadyPaidAmount)})</span>
                  )}
                </p>
                {isOpen && (
                  <p className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded p-3">
                    Uw betaling staat klaar maar is nog niet voltooid. Klik hieronder om te voltooien.
                  </p>
                )}
                <Button onClick={handlePay} disabled={payLoading} className="bg-[#3da5a5] hover:bg-[#2d8585] w-full h-12" data-testid="pay-invoice-btn">
                  {payLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Bezig...</> : <><Euro className="w-4 h-4 mr-2" /> {isOpen ? 'Betaling voltooien' : 'Online betalen via Mollie'}</>}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  Of maak handmatig over op IBAN <strong>NL33INGB0709828160</strong> t.n.v. Cardoso Bouw, o.v.v. factuurnummer {invoice.number}.
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {isPaid && (
          <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white" data-testid="review-after-payment-block">
            <CardContent className="py-6 text-center space-y-3">
              <div className="text-3xl">⭐⭐⭐⭐⭐</div>
              <h3 className="text-lg font-bold text-gray-800">Tevreden? Help andere klanten ons te vinden!</h3>
              <p className="text-sm text-gray-600">
                Een korte Google-review (1 minuut werk) helpt ons enorm en wordt enorm gewaardeerd.
              </p>
              <a
                href="https://g.page/r/Ca4HCnZWgsG8EBE/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 rounded-md font-semibold transition-colors no-underline"
                data-testid="google-review-cta"
              >
                ⭐ Geef ons een Google-review
              </a>
            </CardContent>
          </Card>
        )}

        {isPaid && searchParams.get('from') !== 'portal' && (
          <Card className="border-2 border-[#3da5a5] bg-gradient-to-br from-[#3da5a5]/5 to-white" data-testid="register-after-invoice-block">
            <CardContent className="py-8 text-center space-y-4">
              <div className="w-14 h-14 mx-auto bg-[#3da5a5]/10 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-[#3da5a5]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Bedankt voor uw betaling!</h3>
              <p className="text-sm text-gray-600">
                Wilt u uw werkzaamheden eenvoudig kunnen volgen? Maak een gratis account aan om foto's, voortgang, offertes en facturen op één plek te bekijken.
              </p>
              <Button
                onClick={() => navigate(`/client/register?email=${encodeURIComponent(invoice?.client_email||'')}&phone=${encodeURIComponent(invoice?.client_phone||'')}&name=${encodeURIComponent(invoice?.client_name||'')}`)}
                className="bg-[#3da5a5] hover:bg-[#2d8585] w-full sm:w-auto"
                data-testid="register-after-invoice-btn"
              >
                Gratis account aanmaken
              </Button>
              <p className="text-xs text-gray-400">
                Al een account? <a href="/client" className="text-[#3da5a5] underline">Inloggen via dashboard.cardosobouw.nl/client</a>
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PublicInvoice;
